// eslint-disable-next-line sonarjs/cognitive-complexity
;(function () {
  'use strict'
  /**
   * Main initialization function. Runs once the DOM is ready.
   *
   * Responsibilities:
   * - Queries all required DOM elements (anchor nav, header, hero, section targets).
   * - Filters out anchor links whose target sections don't exist or have no height.
   * - Attaches a click handler to each link for smooth-scroll behavior.
   * - Initializes sticky positioning logic and scroll-based active-section tracking.
   * - Listens for a custom `headerIsHidden` event to adjust sticky top offset
   *   when the header collapses/expands.
   */
  function onDocumentReady() {
    const anchorNavigationWrapper = document.querySelector('.anchornavigation')
    const anchorNavigation = document.querySelector('.cmp-anchor-navigation')
    const anchorNavigationLinks = anchorNavigation?.querySelectorAll('.cmp-anchor-navigation__link')
    const header = document.querySelector('.cmp-header') || document.querySelector('.cmp-header-v2')
    const hero = document.querySelector('.cmp-hero-basic')
    let anchorNavigationSections = []
    let headerIsHidden = false

    if (!header) {
      console.error('Header element not found.')
      return
    }

    const activeClass = 'cmp-anchor-navigation__link--active'
    const stickyClass = 'cmp-anchor-navigation--sticky'
    const overflowingClass = 'cmp-anchor-navigation--overflowing'

    let scroll_position = 0
    let scroll_direction

    /**
     * Listens for the custom `headerIsHidden` event dispatched by the header component.
     * Keeps `headerIsHidden` in sync so sticky offset calculations remain accurate
     * when the header collapses on scroll.
     *
     * @param {CustomEvent} event - Event with `detail.isHidden` boolean.
     */
    window.addEventListener('headerIsHidden', function (event) {
      headerIsHidden = event.detail.isHidden

      if (anchorNavigation?.classList.contains(stickyClass)) {
        const newTop = headerIsHidden ? '0px' : `${header.offsetHeight}px`
        anchorNavigationWrapper.style.top = newTop
      }
    })

    if (anchorNavigation && anchorNavigationLinks) {
      const isGeneric = anchorNavigationWrapper?.classList.contains('anchornavigation--generic')
      const isOverflowing = anchorNavigation.scrollWidth > anchorNavigation.clientWidth

      if (isGeneric) {
        anchorNavigationWrapper.style.margin = `0 -${anchorNavigationWrapper.offsetLeft - 1}px`
      }

      for (const link of anchorNavigationLinks) {
        let id = link.getAttribute('href').slice(1)
        let target = document.querySelector(`[id="${id}"]`)
        if (target && target.getBoundingClientRect().height) {
          anchorNavigationSections.push({ element: target, link })
        } else {
          // Remove links whose target section doesn't exist or has no visible height
          link.parentElement.remove()
        }

        if (isOverflowing && isGeneric) {
          anchorNavigation.classList.add(overflowingClass)
        }

        /**
         * Click handler for each anchor navigation link.
         * Prevents default browser anchor jump, manually calculates the correct
         * scroll target accounting for the sticky header and anchor nav heights,
         * and smoothly scrolls to the section.
         *
         * @param {MouseEvent} event
         */
        link.addEventListener('click', function (event) {
          if (target) {
            event.preventDefault()

            setActiveSection(link)

            const headerHeight = header.offsetHeight
            const anchorHeight = anchorNavigation.offsetHeight

            const currentScrollY = window.scrollY
            const willScrollUp = target.offsetTop < currentScrollY

            const buffer = 20
            // When scrolling up, or when the header is visible, account for both
            // the header and anchor nav heights as offset so the section isn't hidden underneath them.
            const totalOffset =
              willScrollUp || currentScrollY < headerHeight || !headerIsHidden
                ? headerHeight + anchorHeight + buffer
                : anchorHeight + buffer

            const offsetPosition = target.offsetTop - totalOffset

            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth',
            })
          }
        })
      }

      const anchorNavHeight = anchorNavigation.offsetTop

      /**
       * Determines whether the anchor navigation should currently be sticky.
       * Compares the current scroll position (adjusted for both the header and anchor nav
       * heights when the header is visible) against the anchor nav's original vertical
       * position in the document.
       *
       * @returns {boolean} True if the nav should be pinned to the top of the viewport.
       */
      const anchorNavIsSticky = () => {
        const scrollY = window.pageYOffset
        const headerHeight = header.offsetHeight

        return headerIsHidden
          ? scrollY >= anchorNavigationWrapper.offsetTop
          : scrollY + headerHeight + anchorNavigation.offsetHeight >= anchorNavHeight
      }

      /**
       * Scroll event handler. Tracks scroll direction, toggles the sticky class
       * on the anchor navigation, and updates which section link is active.
       *
       * - Adds `stickyClass` and sets `top`/`right` inline styles when the nav
       *   should stick to the viewport.
       * - Removes `stickyClass` and clears inline styles when the nav returns to
       *   its natural document flow position.
       * - For the non-generic variant, offsets the right edge to match the hero component's
       *   horizontal position; for the generic variant, sets `right` to 0.
       */
      const toggleSticky = () => {
        scroll_direction = document.body.getBoundingClientRect().top > scroll_position ? 'up' : 'down'
        scroll_position = document.body.getBoundingClientRect().top

        const shouldBeSticky = anchorNavIsSticky()
        const isCurrentlySticky = anchorNavigation.classList.contains(stickyClass)
        const headerHeight = header.offsetHeight

        if (shouldBeSticky && !isCurrentlySticky) {
          anchorNavigation.classList.add(stickyClass)
          anchorNavigationWrapper.style.top = headerIsHidden ? `0px` : `${headerHeight}px`
          anchorNavigationWrapper.style.right = `${isGeneric ? 0 : hero.getBoundingClientRect().x}px`
        } else if (!shouldBeSticky && isCurrentlySticky) {
          anchorNavigation.classList.remove(stickyClass)
          anchorNavigationWrapper.style.top = ''
          anchorNavigationWrapper.style.right = ''
        } else if (shouldBeSticky && isCurrentlySticky) {
          // Already sticky — only update `top` if the header visibility changed
          const newTop = headerIsHidden ? `0px` : `${headerHeight}px`
          if (anchorNavigationWrapper.style.top !== newTop) {
            anchorNavigationWrapper.style.top = newTop
          }
        }

        updateActiveSection()
      }

      /**
       * Marks the given link as the active navigation item.
       * Removes the active class from all other links, then adds it to the provided link.
       * If the nav is horizontally overflowing, scrolls the nav bar so the active link
       * is centered in view.
       *
       * @param {HTMLElement} link - The anchor link element to activate.
       */
      const setActiveSection = (link) => {
        for (const anchorNavigationLink of anchorNavigationLinks) {
          anchorNavigationLink.blur()
          removeActiveSection(anchorNavigationLink)
        }

        link.classList.add(activeClass)

        if (isOverflowing) {
          anchorNavigation.scroll({
            left: link.offsetLeft - anchorNavigation.clientWidth / 2 + link.clientWidth / 2,
            behavior: 'smooth',
          })
        }
      }

      /**
       * Removes the active class from a single navigation link.
       *
       * @param {HTMLElement} link - The anchor link element to deactivate.
       */
      const removeActiveSection = (link) => {
        link.classList.remove(activeClass)
      }

      /**
       * Determines which section is currently "in view" based on the scroll position
       * and updates the active link accordingly.
       *
       * Strategy:
       * - Calculates an effective scroll position adjusted for sticky header/nav heights
       *   and scroll direction (the offset is larger when scrolling up or when the header
       *   is visible, to avoid flickering).
       * - First checks if the adjusted position falls inside a section's bounds.
       * - Falls back to the section whose top edge is closest to the adjusted position,
       *   so there is always an active link even between sections.
       */
      const updateActiveSection = () => {
        const headerHeight = header.offsetHeight
        const anchorHeight = anchorNavigation.offsetHeight

        const totalOffset = !headerIsHidden || scroll_direction === 'up' ? headerHeight + anchorHeight : anchorHeight

        const scrollPosition = window.scrollY + totalOffset + 50

        let activeSection = null
        let minDistance = Number.POSITIVE_INFINITY

        for (const section of anchorNavigationSections) {
          const sectionTop = section.element.offsetTop
          const sectionBottom = sectionTop + section.element.offsetHeight

          if (scrollPosition >= sectionTop && scrollPosition <= sectionBottom) {
            activeSection = section
            break
          }

          const distance = Math.abs(sectionTop - scrollPosition)
          if (distance < minDistance) {
            minDistance = distance
            activeSection = section
          }
        }

        if (activeSection) {
          setActiveSection(activeSection.link)
        }
      }

      updateActiveSection()

      if (window.scrollY > 0) {
        toggleSticky()
      }

      window.addEventListener('scroll', toggleSticky)
    }
  }

  if (document.readyState !== 'loading') {
    onDocumentReady()
  } else {
    document.addEventListener('DOMContentLoaded', onDocumentReady)
  }
})()
